/**
 * Stock Cells Grid Component
 * 
 * Displays abstracted stock locations as simplified cards
 * Protects business secrets by showing stock1, stock2, etc. instead of real locations
 */

// No hooks used here
import { __ } from '@wordpress/i18n';
import QuantitySelector from './QuantitySelector';

const StockCellsGrid = ({ stockCells, selectedPicks, onStockSelection, isMobile }) => {
    
    /**
     * Get current quantity for a stock cell
     */
    const getCurrentQuantity = (stockId) => {
        const pick = selectedPicks.find(p => p.stock_id === stockId);
        return pick ? pick.qty : 0;
    };
    
    /**
     * Handle quantity change from QuantitySelector
     */
    const handleQuantityChange = (stockId, quantity) => {
        // QuantitySelector already handles validation, so we can use quantity directly
        onStockSelection(stockId, quantity);
    };
    
    if (!stockCells || stockCells.length === 0) {
        return (
            <div className="fos-stock-section">
                <div style={{ 
                    textAlign: 'center', 
                    padding: '40px 20px', 
                    color: '#666',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    border: '2px dashed #dee2e6'
                }}>
                    <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📦</div>
                    <p style={{ margin: '0 0 8px', fontWeight: '500' }}>
                        {__('此變體沒有庫存', 'frontend-order-system')}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.9rem' }}>
                        {__('可能需要預訂或選擇其他變體', 'frontend-order-system')}
                    </p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="fos-stock-section fos-fade-in">
            <h4 className="fos-stock-title">
                {__('選擇庫存位置', 'frontend-order-system')}
            </h4>
            <div className="fos-stock-grid">
                {stockCells.map((stock) => {
                    const currentQty = getCurrentQuantity(stock.stock_id);
                    const hasSelection = currentQty > 0;
                    const isLowStock = stock.available_qty <= 5;
                    const isOutOfStock = stock.available_qty === 0;
                    
                    return (
                        <div
                            key={stock.stock_id}
                            className={`fos-stock-cell ${hasSelection ? 'has-selection' : ''}`}
                        >
                            <div className="fos-stock-header">
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    flexWrap: 'nowrap',
                                    gap: '8px',
                                    minHeight: '32px'
                                }}>
                                    <div className="fos-stock-name">
                                        📦 {stock.display_name}
                                    </div>
                                    
                                    {/* Expiry Date Display on same line */}
                                    {stock.expiry_date && stock.expiry_date.trim() !== '' && (
                                        <div style={{
                                            padding: '2px 6px',
                                            borderRadius: '3px',
                                            fontSize: '0.75rem',
                                            background: stock.expiry_status === 'expired' ? '#f8d7da' :
                                                      stock.expiry_status === 'expiring_soon' ? '#fff3cd' :
                                                      stock.expiry_status === 'expiring_medium' ? '#cce5ff' : '#f8f9fa',
                                            border: `1px solid ${
                                                stock.expiry_status === 'expired' ? '#f5c6cb' :
                                                stock.expiry_status === 'expiring_soon' ? '#ffeaa7' :
                                                stock.expiry_status === 'expiring_medium' ? '#b3d9ff' : '#e1e1e1'
                                            }`,
                                            color: stock.expiry_status === 'expired' ? '#721c24' :
                                                   stock.expiry_status === 'expiring_soon' ? '#856404' :
                                                   stock.expiry_status === 'expiring_medium' ? '#0056b3' : '#666',
                                            fontWeight: '500',
                                            whiteSpace: 'nowrap',
                                            flexShrink: 0
                                        }}>
                                            📅 {stock.expiry_date}
                                            {stock.expiry_status === 'expired' && (
                                                <span style={{ 
                                                    marginLeft: '4px', 
                                                    fontSize: '0.7rem',
                                                    fontWeight: 'bold',
                                                    color: '#dc3545'
                                                }}>
                                                    ⚠️
                                                </span>
                                            )}
                                            {stock.expiry_status === 'expiring_soon' && (
                                                <span style={{ 
                                                    marginLeft: '4px', 
                                                    fontSize: '0.7rem',
                                                    fontWeight: 'bold',
                                                    color: '#ffc107'
                                                }}>
                                                    ⏰
                                                </span>
                                            )}
                                            {stock.expiry_status === 'expiring_medium' && (
                                                <span style={{ 
                                                    marginLeft: '4px', 
                                                    fontSize: '0.7rem',
                                                    fontWeight: '500',
                                                    color: '#17a2b8'
                                                }}>
                                                    📆
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {!isOutOfStock && (
                                <div className="fos-quantity-selector">
                                    <QuantitySelector
                                        quantity={currentQty}
                                        maxQuantity={stock.max_pick_qty}
                                        minQuantity={0}
                                        onChange={(qty) => handleQuantityChange(stock.stock_id, qty)}
                                        title=""
                                        showQuickSelect={false}
                                        showMaxLabel={false}
                                        isMobile={isMobile}
                                        size="small"
                                        className="fos-stock-quantity-selector"
                                        style={{ marginBottom: '8px' }}
                                    />
                                    
                                    
                                    {/* Warning for low stock */}
                                    {isLowStock && currentQty > 0 && (
                                        <div style={{
                                            marginTop: '8px',
                                            padding: '4px 8px',
                                            background: '#fff3cd',
                                            color: '#856404',
                                            borderRadius: '4px',
                                            fontSize: '0.8rem',
                                            textAlign: 'center'
                                        }}>
                                            ⚠️ {__('庫存較少', 'frontend-order-system')}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {/* Out of Stock Message */}
                            {isOutOfStock && (
                                <div style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    background: '#f8d7da',
                                    color: '#721c24',
                                    borderRadius: '4px',
                                    fontSize: '0.9rem'
                                }}>
                                    {__('此位置目前無庫存', 'frontend-order-system')}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            
        </div>
    );
};

export default StockCellsGrid;
